import { create } from 'zustand'
import { api } from '@/lib/api'

interface CronExecution {
  id: string
  cronName: string
  scheduledAt: string
  startedAt: string
  completedAt: string | null
  durationMs: number | null
  success: boolean
  itemsProcessed: number | null
  errors: string | null
  metadata: unknown
  createdAt: string
}

interface CronJobStats {
  cronName: string
  totalRuns: number
  successCount: number
  failureCount: number
  successRate: number
  avgDurationMs: number
  lastRun: string | null
  lastSuccess: string | null
}

interface HealthMetrics {
  database: { status: string; latencyMs: number }
  users: { total: number; new24h: number; new7d: number }
  api: { requests24h: number; errorRate24h: number; avgResponseTimeMs: number }
  cron: { executions24h: number; failures24h: number }
  waitlist: { pending: number }
  storage: { status: string; sampleObjects: number; sampleSize: number }
  timestamp: string
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

interface SystemState {
  cronExecutions: CronExecution[]
  cronPagination: Pagination
  isLoadingCronExecutions: boolean

  cronStats: { jobs: CronJobStats[]; recentFailures: CronExecution[] } | null
  isLoadingCronStats: boolean

  healthMetrics: HealthMetrics | null
  isLoadingHealth: boolean

  cronNameFilter: string

  triggeringCron: string | null

  error: string | null

  setCronNameFilter: (name: string) => void
  fetchCronExecutions: (page?: number) => Promise<void>
  fetchCronStats: () => Promise<void>
  fetchHealthMetrics: () => Promise<void>
  triggerCron: (cronName: string) => Promise<{ success: boolean; error?: string }>
}

export const useSystemStore = create<SystemState>((set, get) => ({
  cronExecutions: [],
  cronPagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
  isLoadingCronExecutions: false,

  cronStats: null,
  isLoadingCronStats: false,

  healthMetrics: null,
  isLoadingHealth: false,

  cronNameFilter: '',

  triggeringCron: null,

  error: null,

  setCronNameFilter: (cronNameFilter) => {
    set({ cronNameFilter })
    get().fetchCronExecutions(1)
  },

  fetchCronExecutions: async (page = 1) => {
    set({ isLoadingCronExecutions: true, error: null })
    const { cronNameFilter } = get()
    try {
      const res = await api.get<{
        data: { executions: CronExecution[]; pagination: Pagination }
      }>('/api/v1/admin/system/cron/executions', {
        page,
        limit: 50,
        cronName: cronNameFilter || undefined,
      })
      set({
        cronExecutions: res.data.executions,
        cronPagination: res.data.pagination,
        isLoadingCronExecutions: false,
      })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingCronExecutions: false })
    }
  },

  fetchCronStats: async () => {
    set({ isLoadingCronStats: true, error: null })
    try {
      const res = await api.get<{
        data: { jobs: CronJobStats[]; recentFailures: CronExecution[] }
      }>('/api/v1/admin/system/cron/stats')
      set({ cronStats: res.data, isLoadingCronStats: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingCronStats: false })
    }
  },

  fetchHealthMetrics: async () => {
    set({ isLoadingHealth: true, error: null })
    try {
      const res = await api.get<{ data: HealthMetrics }>('/api/v1/admin/system/health/metrics')
      set({ healthMetrics: res.data, isLoadingHealth: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingHealth: false })
    }
  },

  triggerCron: async (cronName: string) => {
    set({ triggeringCron: cronName, error: null })
    try {
      const res = await api.post<{ success: boolean; data: { error?: string } }>(
        '/api/v1/admin/system/cron/trigger',
        { cronName }
      )
      // Refetch stats and executions to show the new entry
      await Promise.all([get().fetchCronStats(), get().fetchCronExecutions(1)])
      set({ triggeringCron: null })
      return { success: res.success }
    } catch (err) {
      const message = (err as Error).message
      set({ error: message, triggeringCron: null })
      return { success: false, error: message }
    }
  },
}))
